import React, { useState, useEffect, useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type CookieCategory = 'essential' | 'analytics' | 'marketing';

export interface CookieConsent {
  essential: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export interface CookieBannerProps {
  /** Privacy policy page URL */
  privacyUrl?: string;
  /** Site name for the banner text */
  siteName?: string;
  /**
   * Appelé à chaque choix. `precedent` est le choix d'avant (null au premier) :
   * c'est lui qui dit qu'un accord vient d'être RETIRÉ, après une réouverture
   * par `ouvrirPreferencesCookies()` (KST-2923).
   */
  onConsentChange?: (consent: CookieConsent, precedent: CookieConsent | null) => void;
  /**
   * Texte du bandeau, à la place du texte par défaut (KST-2923). Il doit nommer
   * TOUT ce qu'« Accepter » ouvre : sur remup.fr, le texte par défaut ne parlait
   * que d'analyse alors que le bouton ouvrait aussi Meta, TikTok et Google Ads.
   * Le lien vers la politique de confidentialité reste posé par le composant.
   */
  texte?: React.ReactNode;
  /** Texte court du mode `compactMobile`, sous `sm` ; à défaut, `texte`. */
  texteCompact?: React.ReactNode;
  /**
   * Délai avant d'afficher le bandeau, en ms. Défaut 1 500 : le contenu s'affiche
   * d'abord. Mettre 0 quand la page vise un LCP court : sur mobile le paragraphe
   * du bandeau est le plus grand bloc de texte de l'écran, donc l'élément LCP, et
   * le délai se retrouve intégralement dans la mesure (remup, KST-1958).
   */
  delayMs?: number;
  /**
   * Habillage de chaque zone du bandeau, pour l'aligner sur la charte d'un site
   * (remup.fr, KST-2130) sans toucher aux autres : sans cette prop, le rendu est
   * exactement celui d'avant.
   *
   * Une zone renseignée REMPLACE l'habillage gris par défaut de cette zone
   * (couleurs, fond, bordure, arrondi), la structure (marges, taille, flex)
   * restant au composant. Les classes ne s'ajoutaient d'abord qu'aux couleurs par
   * défaut : le composant ne fait pas de fusion Tailwind, et sur remup.fr le
   * `text-blue-600` du lien l'emportait sur le `text-accent` passé — le lien du
   * bandeau sortait dans le bleu de l'ancien gabarit. Une zone renseignée doit
   * donc porter son habillage complet (ex. `text-white` avec un `bg-…` sombre).
   */
  classes?: {
    conteneur?: string;
    texte?: string;
    lien?: string;
    boutonSecondaire?: string;
    boutonPrimaire?: string;
  };
  /**
   * Version courte sur mobile (remup.fr, KST-2270) : une ligne et deux boutons
   * courts de même taille, au lieu d'un paragraphe et de deux gros boutons qui
   * couvraient un quart de l'écran — par-dessus le calendrier et le formulaire.
   * 243 sessions sur 3 734 seulement le fermaient sur /lp/rdv (05→11/10/2026).
   * Les boutons gardent 44 px de haut (cible tactile) et le bandeau ~60 px :
   * on rogne le libellé et les marges, jamais la zone qu'on touche.
   * Ordinateur inchangé ; sans la prop, rendu inchangé partout.
   *
   * KST-2923 : le texte passe de 12 à 14 px et au-dessus des boutons,
   * qui se partagent la largeur. 3,6 % des visiteurs de /lp/rdv répondaient au
   * bandeau dans les navigateurs Instagram et Facebook : une ligne en 12 px ne
   * se lisait pas, et un consentement qu'on ne lit pas n'est pas éclairé.
   */
  compactMobile?: boolean;
}

// ============================================================================
// CONSENT HELPERS (exported for use by analytics components)
// ============================================================================

const CONSENT_KEY = 'cookie-consent';

/** Get current cookie consent from localStorage */
export function getCookieConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookieConsent;
  } catch {
    return null;
  }
}

/** Check if a specific cookie category has been accepted */
export function hasConsent(category: CookieCategory): boolean {
  if (category === 'essential') return true;
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent[category] === true;
}

/**
 * Événement qui rouvre le bandeau (lien « Gérer les cookies », KST-2923). La
 * politique de confidentialité promettait qu'on pouvait changer d'avis « à tout
 * moment via ce bandeau » : rien ne permettait de le rouvrir une fois répondu.
 */
export const EVENEMENT_PREFERENCES_COOKIES = 'kst:cookie-preferences';

/** Rouvre le bandeau, même si un choix est déjà enregistré. */
export function ouvrirPreferencesCookies(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(EVENEMENT_PREFERENCES_COOKIES));
}

/** Save consent to localStorage */
function saveConsent(consent: CookieConsent): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Bouton du bandeau compact : 44 px de haut sur mobile (cible tactile minimale),
 * libellé court ; à partir de `sm`, la taille d'origine.
 */
const BOUTON_COMPACT = 'inline-flex min-h-11 flex-1 items-center justify-center px-3.5 text-sm sm:min-h-0 sm:flex-none sm:px-4 sm:py-2';

/**
 * Shared Cookie consent banner for all Konstellation sites.
 * Supports cookie categories (essential, analytics, marketing).
 * Persists consent in localStorage.
 */
const CookieBanner: React.FC<CookieBannerProps> = ({
  privacyUrl = '/politique-confidentialite',
  siteName,
  onConsentChange,
  delayMs = 1500,
  compactMobile = false,
  classes = {},
  texte,
  texteCompact,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      if (delayMs <= 0) {
        setIsVisible(true);
        return;
      }
      const timer = setTimeout(() => setIsVisible(true), delayMs);
      return () => clearTimeout(timer);
    }
  }, [delayMs]);

  // Réouverture à la demande, choix déjà enregistré ou non.
  useEffect(() => {
    const rouvrir = () => setIsVisible(true);
    window.addEventListener(EVENEMENT_PREFERENCES_COOKIES, rouvrir);
    return () => window.removeEventListener(EVENEMENT_PREFERENCES_COOKIES, rouvrir);
  }, []);

  const handleConsent = useCallback((analytics: boolean, marketing: boolean) => {
    const consent: CookieConsent = {
      essential: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    };
    const precedent = getCookieConsent();
    saveConsent(consent);
    setIsVisible(false);
    onConsentChange?.(consent, precedent);
  }, [onConsentChange]);

  const acceptAll = useCallback(() => handleConsent(true, true), [handleConsent]);
  const acceptEssentialOnly = useCallback(() => handleConsent(false, false), [handleConsent]);

  if (!isVisible) return null;

  return (
    // `data-kst-cookie-banner` : la barre « Prendre RDV » s'efface tant que le
    // bandeau occupe le bas de l'écran (KST-2270).
    <div
      data-kst-cookie-banner=""
      className={`fixed bottom-0 left-0 right-0 z-50 ${compactMobile ? 'px-3 py-2 sm:p-4' : 'p-4'} backdrop-blur-sm border-t shadow-lg ${classes.conteneur ?? 'bg-white/95 border-gray-200'}`}
    >
      <div className={`max-w-5xl mx-auto flex ${compactMobile ? 'flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-4' : 'flex-col sm:flex-row items-start sm:items-center gap-4'}`}>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${classes.texte ?? 'text-gray-600'}`}>
            {compactMobile && <span className="sm:hidden">{texteCompact ?? texte ?? <>Cookies d&apos;analyse pour améliorer le site.</>} </span>}
            <span className={compactMobile ? 'hidden sm:inline' : undefined}>
              {texte ?? <>
                {siteName ? `${siteName} utilise` : 'Ce site utilise'} des cookies essentiels au
                fonctionnement du site et des cookies d&apos;analyse pour améliorer votre expérience.
              </>}{' '}
            </span>
            <a href={privacyUrl} className={`hover:underline ${classes.lien ?? 'text-blue-600'}`}>
              {compactMobile ? <><span className="sm:hidden">En savoir plus</span><span className="hidden sm:inline">Politique de confidentialité</span></> : 'Politique de confidentialité'}
            </a>
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {/* Refuser et accepter de même taille : le refus doit être aussi simple que l'acceptation. */}
          <button
            onClick={acceptEssentialOnly}
            className={`${compactMobile ? BOUTON_COMPACT : 'px-4 py-2 text-sm'} border transition-colors ${classes.boutonSecondaire ?? 'rounded-md border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            {compactMobile ? <><span className="sm:hidden">Refuser</span><span className="hidden sm:inline">Essentiels uniquement</span></> : 'Essentiels uniquement'}
          </button>
          <button
            onClick={acceptAll}
            className={`${compactMobile ? BOUTON_COMPACT : 'px-4 py-2 text-sm'} transition-colors ${classes.boutonPrimaire ?? 'rounded-md bg-gray-900 text-white hover:bg-gray-800'}`}
          >
            {compactMobile ? <><span className="sm:hidden">Accepter</span><span className="hidden sm:inline">Tout accepter</span></> : 'Tout accepter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
export { CookieBanner };
